/**
 * Whether a redacted output still reflects the current redaction state
 * (VE-25870). Downloading a stale output is how a partially-redacted video
 * reached a public records release in VE-25648.
 *
 * The verdict itself is trivial — a list of reasons, any of which makes an output
 * stale. What is not trivial is establishing the first reason, that saved
 * redactions exist which the output does not contain, and callers answer that
 * one of two ways:
 *
 * - **Version label**, for anything saved since VE-25870 / VE-26422. Redact File
 *   tells the engine which version to produce and writes that same number onto
 *   the redaction data it consumes, so the two sides can be compared without
 *   either having observed the other. See `readBurnInLabel`.
 * - **Timestamps**, for saves written before that. Did anything land after the
 *   output was created? The folder view and the document editor anchor on the
 *   output itself, which appears only once the engine or the renderer has
 *   finished — minutes after anything the same click wrote — so no tolerance is
 *   needed. The audio/video tab anchors on the burn-in's own selections asset
 *   instead, to catch saves made while a long engine job runs, and pays for that
 *   with the fences described there.
 *
 * Any newer save makes the output stale, which is less blunt than it sounds. A
 * save records which series are selected, and only selected series are sent to
 * the blur engine (`selectUdrRedactions` filters on exactly that), so changing
 * the selection changes what comes out. There is no such thing as a save that
 * moves the redaction data without moving the output. Known limitation: select
 * then unselect leaves no net change and still warns — recognising that needs a
 * real diff of the two bodies, which nothing here does.
 *
 * A redaction-count comparison used to sit between the two. It is gone: it could
 * not see a redaction merely moved or resized, and both surfaces that used it now
 * have something exact or something simpler.
 */

/**
 * More than one can apply at once. `unsavedChanges` is kept distinct from
 * `missingLatestEdits` because the copy differs: one says the edits aren't
 * persisted yet, the other that they are saved but not burned in.
 */
export type StaleReason =
  | 'missingLatestEdits'
  | 'unsavedChanges'
  | 'newerVersionInProgress';

export interface RedactedFileStaleness {
  readonly isStale: boolean;
  readonly reasons: readonly StaleReason[];
  readonly version: number | undefined;
}

/** Minimum shape of a redaction-data asset record needed to compare versions. */
export interface RedactionDataRecord {
  readonly id?: string;
  readonly createdDateTime: string;
  readonly details?: unknown;
}

/**
 * How a redacted output points back at what produced it, as the blur engine
 * writes it onto `redacted-media.details`. Only the selections pointer is used:
 * it dates the burn-in for outputs too old to carry a version label.
 */
export interface RedactionProvenance {
  /** Names the `redaction-user-selections` asset the burn-in consumed. */
  readonly redactionAssetId?: string;
}

const readId = (record: Record<string, unknown>, key: string) => {
  const value = record[key];
  return typeof value === 'string' && value ? value : undefined;
};

export function readRedactionProvenance(details: unknown): RedactionProvenance {
  if (!details || typeof details !== 'object') {
    return {};
  }
  const record = details as Record<string, unknown>;
  return { redactionAssetId: readId(record, 'redactionAssetId') };
}

/**
 * What a `redaction-media-data` asset says about its own relationship to a
 * burn-in. Three states, and the difference between the last two is the whole
 * reason `burnInRequested` is written even when false:
 *
 * - `burnedIn` — sent for burn-in, and names the output version it was sent to
 *   produce. Compare that against the output's own `version`.
 * - `saved` — an ordinary save, never sent. Certainly not in any output.
 * - `unlabelled` — written before this scheme existed, or by a writer that has no
 *   labels at all (documents). Says nothing either way.
 */
export type BurnInLabel =
  | { readonly state: 'burnedIn'; readonly version: number }
  | { readonly state: 'saved' }
  | { readonly state: 'unlabelled' };

export function readBurnInLabel(details: unknown): BurnInLabel {
  if (!details || typeof details !== 'object') {
    return { state: 'unlabelled' };
  }
  const record = details as Record<string, unknown>;
  if (record.burnInRequested === false) {
    return { state: 'saved' };
  }
  if (record.burnInRequested !== true) {
    return { state: 'unlabelled' };
  }
  // Requested but carrying no version: the label write partly failed. Treated as
  // unlabelled so another method answers rather than nothing does.
  return typeof record.expectedRedactedVersion === 'number'
    ? { state: 'burnedIn', version: record.expectedRedactedVersion }
    : { state: 'unlabelled' };
}
/** Newest by timestamp. Unparseable stamps are skipped. */
function pickNewest<T>(
  records: readonly T[] | undefined,
  timestampOf: (record: T) => string | null | undefined
): T | null {
  let best: T | null = null;
  let bestMs = -Infinity;
  for (const record of records ?? []) {
    const recordMs = new Date(timestampOf(record) ?? '').getTime();
    if (Number.isNaN(recordMs) || recordMs <= bestMs) {
      continue;
    }
    best = record;
    bestMs = recordMs;
  }
  return best;
}

const createdDateTimeOf = (record: RedactionDataRecord) =>
  record.createdDateTime;
/** Newest save, by timestamp rather than the array's order. */
export const pickNewestRedactionData = (
  records: readonly RedactionDataRecord[] | undefined
): RedactionDataRecord | null => pickNewest(records, createdDateTimeOf);

/** Newest of anything else timestamped, e.g. a file's output assets. */
export const pickNewestBy = pickNewest;

export interface RedactedFileStalenessInput {
  /** False when nothing has been burned in — there is then nothing to warn on. */
  readonly hasOutput: boolean;
  /**
   * There is saved redaction data the output does not contain. The caller
   * establishes this however its surface can — a version label, or timestamps for
   * outputs too old to carry one — and owns any safety margin on the comparison;
   * see `savedAfterLegacyBurnIn` in the audio/video selector.
   */
  readonly savedAfterBurnIn?: boolean;
  /**
   * The editor has edits that aren't persisted yet. Both editors pass their dirty
   * flag: the document editor's is set only by redaction mutations, the audio/
   * video editor's `tdoIsChanged` also by selecting a series — which belongs
   * there, since selection decides what the blur engine receives. Known
   * limitation: select then unselect leaves no net change and still warns.
   */
  readonly hasUnsavedChanges?: boolean;
  /**
   * Global settings were saved after the output's redaction data was. Those
   * settings feed the blur, so the output no longer reflects them. Simple by
   * design: it fires on a settings save that changed nothing, which is accepted
   * for now.
   */
  readonly settingsChangedAfterBurnIn?: boolean;
  readonly isBurnInRunning?: boolean;
  readonly version?: number;
}

export function computeRedactedFileStaleness({
  hasOutput,
  savedAfterBurnIn = false,
  hasUnsavedChanges = false,
  settingsChangedAfterBurnIn = false,
  isBurnInRunning = false,
  version,
}: RedactedFileStalenessInput): RedactedFileStaleness {
  if (!hasOutput) {
    return { isStale: false, reasons: [], version };
  }

  const reasons: StaleReason[] = [];

  if (hasUnsavedChanges) {
    reasons.push('unsavedChanges');
  }

  if (settingsChangedAfterBurnIn || savedAfterBurnIn) {
    reasons.push('missingLatestEdits');
  }

  if (isBurnInRunning) {
    reasons.push('newerVersionInProgress');
  }

  return { isStale: reasons.length > 0, reasons, version };
}

/**
 * The banner copy for a verdict, or `null` for no banner. An in-progress burn-in
 * suppresses it — that state has its own banner and showing both is confusing.
 */
export const staleBannerMessageId = (
  reasons: readonly StaleReason[]
): string | null => {
  if (reasons.includes('newerVersionInProgress')) {
    return null;
  }
  if (reasons.includes('unsavedChanges')) {
    return 'staleRedactedFileUnsavedBanner';
  }
  return reasons.includes('missingLatestEdits')
    ? 'staleRedactedFileBanner'
    : null;
};

/** Stable object for callers that need a verdict before data loads. */
export const NOT_STALE: RedactedFileStaleness = {
  isStale: false,
  reasons: [],
  version: undefined,
};
